import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { I18nContextProvider } from '@ecg-marktplaats/js-react-i18n';
import { Header } from '@hz-design-system/web-ui';

import LoginNudgeApp from '../../apps/LoginNudgeApp/LoginNudgeApp';
import ListingApp from '../../apps/ListingApp/ListingApp';
import DescriptionButtonApp from '../../apps/DescriptionButtonApp/DescriptionButtonApp';
import SellerSidebarApp from '../../apps/SellerSidebarApp/SellerSidebarApp';
import AdmarktSimilarItemsApp from '../../apps/AdmarktSimilarItemsApp/AdmarktSimilarItemsApp';
import ReportApp from '../../apps/ReportApp/ReportApp';
import AdsenseApp from '../../apps/AdsenseApp/AdsenseApp';
import { BANNER_POSITION, bannerPositions } from '../../apps/BannersApp/constants/banners';
import BannersApp from '../../apps/BannersApp/BannersApp';

import '../../../scss/global.scss';
import '../../../scss/layout.scss';

// server side only components styles for this page
import '../../../../server/pages/vip.styles';

export default (i18n) => {
  const appState = window.__CONFIG__ || {};
  const environment = window.__ENV_CONFIG__ || {};
  const headerConfig = window.__HEADER_CONFIG__ || {};
  const {
    listing: {
      flags: { showBannersOnVip, showExternalAds, showSellerOtherItems, requestFeeds, withBannersFromSeller },
    },
  } = appState;

  const smartBannerAndroidRoot = document.getElementById('smart-banner-android-root');
  const headerRootContainer = document.getElementById('header-root');
  const loginNudgeRoot = document.getElementById('login-nudge-root');
  const listingRoot = document.getElementById('listing-root');
  const descriptionSellerWebsiteLinkRoot = document.getElementById('description-seller-website-link-root');
  const descriptionButtonRoot = document.getElementById('description-button-root');
  const sellerSidebarRoot = document.getElementById('seller-sidebar-root');
  const similarItemsRoot = document.getElementById('similar-items-root');
  const reportRoot = document.getElementById('report-root');
  const sellerOtherItemsRoot = document.getElementById('seller-other-items-root');
  const adsenseRootContainer = document.getElementById('adsense-root');
  const sellerInfoExpandedRoot = document.getElementById('seller-info-expanded-root');
  const sellerLocationRootContainer = document.getElementById('seller-location-root');

  // Render smart banner for Android
  // in case when this is not an Android there is no element to hydrate
  if (environment?.hasSmartBannerAndroidEnabled) {
    const loadComponent = async () => {
      try {
        const SmartBannerAndroidApp = (
          await import(
            /* webpackChunkName: "SmartBannerAndroidApp" */ '../../apps/SmartBannerAndroidApp/SmartBannerAndroidApp'
          )
        ).default;
        hydrateRoot(smartBannerAndroidRoot!, <SmartBannerAndroidApp />);
      } catch {}
    };
    loadComponent();
  }

  // Header is server side rendered in express-react library
  hydrateRoot(headerRootContainer!, <Header headerConfig={headerConfig} />);

  hydrateRoot(
    loginNudgeRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <LoginNudgeApp environment={environment} />
    </I18nContextProvider>,
  );

  hydrateRoot(
    listingRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <ListingApp appState={appState} environment={environment} />
    </I18nContextProvider>,
  );

  import('../../apps/DescriptionSellerWebsiteApp/DescriptionSellerWebsiteLinkApp').then(
    ({ default: DescriptionSellerWebsiteLinkApp }) =>
      hydrateRoot(
        descriptionSellerWebsiteLinkRoot!,
        <I18nContextProvider i18nInstance={i18n}>
          <DescriptionSellerWebsiteLinkApp appState={appState} environment={environment} />
        </I18nContextProvider>,
      ),
  );

  hydrateRoot(
    descriptionButtonRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <DescriptionButtonApp />
    </I18nContextProvider>,
  );

  hydrateRoot(
    sellerSidebarRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <SellerSidebarApp appState={appState} environment={environment} showBannersOnVip={showBannersOnVip} />
    </I18nContextProvider>,
  );

  hydrateRoot(
    similarItemsRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <AdmarktSimilarItemsApp appState={appState} environment={environment} />
    </I18nContextProvider>,
  );

  hydrateRoot(
    reportRoot!,
    <I18nContextProvider i18nInstance={i18n}>
      <ReportApp appState={appState} environment={environment} />
    </I18nContextProvider>,
  );

  if (sellerInfoExpandedRoot) {
    import('../../apps/SellerInfoExpandedApp/SellerInfoExpandedApp').then(({ default: SellerInfoExpandedApp }) =>
      hydrateRoot(
        sellerInfoExpandedRoot!,
        <I18nContextProvider i18nInstance={i18n}>
          <SellerInfoExpandedApp appState={appState} environment={environment} />
        </I18nContextProvider>,
      ),
    );
  }

  if (showSellerOtherItems && sellerOtherItemsRoot) {
    import('../../apps/SellerOtherItemsApp/SellerOtherItemsApp').then(({ default: SellerOtherItemsApp }) =>
      hydrateRoot(
        sellerOtherItemsRoot!,
        <I18nContextProvider i18nInstance={i18n}>
          <SellerOtherItemsApp appState={appState} environment={environment} />
        </I18nContextProvider>,
      ),
    );
  }

  if (showExternalAds) {
    const adsenseRoot = createRoot(adsenseRootContainer!);
    adsenseRoot.render(
      <I18nContextProvider i18nInstance={i18n}>
        <AdsenseApp appState={appState} environment={environment} />
      </I18nContextProvider>,
    );
  }

  import('../../apps/SellerLocationApp/SellerLocationSectionApp').then(({ default: SellerLocationSectionApp }) =>
    hydrateRoot(
      sellerLocationRootContainer!,
      <I18nContextProvider i18nInstance={i18n}>
        <SellerLocationSectionApp environment={environment} appState={appState} />
      </I18nContextProvider>,
    ),
  );

  // Render banners
  // in case when banners are disabled there is no element to hydrate
  if (showBannersOnVip) {
    Object.values(BANNER_POSITION.STATIC).forEach((position) => {
      const bannersRootContainer = document.getElementById(bannerPositions[position].containerId);
      hydrateRoot(
        bannersRootContainer!,
        <I18nContextProvider i18nInstance={i18n}>
          <BannersApp position={position} environment={environment} />
        </I18nContextProvider>,
      );
    });
  }

  if (withBannersFromSeller) {
    const bannersRootContainer = document.getElementById(
      bannerPositions[BANNER_POSITION.DYNAMIC.DEALER_TOP].containerId,
    );
    hydrateRoot(
      bannersRootContainer!,
      <I18nContextProvider i18nInstance={i18n}>
        <BannersApp position={BANNER_POSITION.DYNAMIC.DEALER_TOP} environment={environment} />
      </I18nContextProvider>,
    );
  }

  if (requestFeeds) {
    const loadComponent = async () => {
      try {
        const FeedApp = (await import(/* webpackChunkName: "FeedApp" */ '../../apps/FeedApp/FeedApp')).default;
        const feedRoot = document.getElementById('feed-root');
        hydrateRoot(
          feedRoot!,
          <I18nContextProvider i18nInstance={i18n}>
            <FeedApp appState={appState} environment={environment} />
          </I18nContextProvider>,
        );
      } catch {}
    };
    loadComponent();
  }
};
